<script setup>
definePage({
  meta: {
    layout: "blank",
    unauthenticatedOnly: false,
  },
})
import { langTypes } from "@/utils/constant"
import StudentService from "@/services/StudentService"
import QuestionWrap from "@/components/question/Wrap.vue"
import ContentError from "@/components/common/ContentError.vue"
import BtnLang from "@/components/BtnLang.vue"
import { useCustomSwal } from "@/composables/usePopup"
import { useSurveyStore } from "@/stores/surveyStore"
import { formatFormTypeName } from "@/utils"
const { showCustomSwal } = useCustomSwal()

const CAMPAIGN = import.meta.env.VITE_CAMPAIGN
const loading = ref(false)

const block1 = [
  {
    id: "",
    type: "alert",
    content: `
    <p>Please note: if you spend more than 30 minutes on this page or leave this tab open for too long, your session will expire and your answers will not be saved.</p>
<p>If you do not receive a confirmation email after submitting your form, it means your answers were not taken into account.</p>
<p>Feel free to contact us by email if you have any questions or difficulties: <a href="mailto:mastersurvey@eduniversal-group.com"><u>mastersurvey@eduniversal-group.com</u></a></p>
    `,
  },
]

// DBA Student Satisfaction Survey (form_type id=6) — eligibility/scoring/
// confidentiality note, hardcoded here (not a backend info_block) and
// shown below the title, above block1's session-timeout warning.
const dbaIntroBlock = [
  {
    id: "",
    type: "info",
    content: `
    <p><b>Who should complete this survey?</b></p>
<p>This survey is intended for current DBA candidates in the research/thesis phase and recent DBA graduates. To reach a sufficient number of responses, the programme director may invite candidates and graduates from the most recent relevant intakes and graduating years, according to the size and structure of the programme.</p>
<p>A minimum of 10 valid responses is required for the satisfaction score to be calculated. If the invited population does not reach this threshold, the programme director may extend the invitation to additional intakes or graduating cohorts until the threshold is met.</p>
<p><b>Scoring rule:</b> if fewer than 10 valid responses are collected, a default score of 2.5/5 is assigned, regardless of the number of intakes or cohorts invited.</p>
<p><b>Confidentiality:</b> All responses are strictly anonymous. Individual answers are never shared with your institution. Only aggregated scores are used in the ranking.</p>
    `,
  },
]

const block2 = [
  {
    id: "",
    type: "info",
    content: `<div>
                ${$gettext("If you have not completed this program: do not respond to the form and contact us as soon as possible by sending us an email to:"
                )} <a href="mailto:mastersurvey@eduniversal-group.com"><u>mastersurvey@eduniversal-group.com</u></a> 
                ${$gettext("or by text / WhatsApp at ")}
                <a href="tel:0630231266"><u>06 30 23 12 66</u></a> / <a href="tel:0644154471"><u>06 44 15 44 71</u></a>
            </div>`,
  },
]

const router = useRouter()
const route = useRoute()
const { params } = route
const refVForm = ref()
const isConfirmDialogVisible = ref(false)
const successNoti = ref(false)
const errorNoti = ref(false)
const messageNoti = ref("")
const errorData = ref(null)

const info = ref({})
const selectedAnswers = ref({})
const steps = ref([])
const openedPanels = ref([])
const loadingSubmit = ref(false)
const nickName = ref(null)
const answered = ref(false)
const isAdmin = computed(() => route.query.isAdmin)

// support check error
function getAllSiblings(element) {
  let result = [],
    node = element.parentNode.firstChild

  while (node) {
    if (node !== element && node.nodeType === Node.ELEMENT_NODE)
      result.push(node)
    node = node.nextElementSibling || node.nextSibling
  }
  return result
}
function findQuestionTitle(element) {
  const found = getAllSiblings(element).find((e) => {
    return [...e.classList].includes("question-title")
  })
  if (found) return found
  if (element.parentNode.tagName.toLowerCase() === "body") return null
  return found ?? findQuestionTitle(element.parentNode)
}
function scrollToElement(element) {
  if (!element) return

  const yOffset = -200
  const y = element.getBoundingClientRect().top + window.scrollY + yOffset

  window.scrollTo({ top: y, behavior: "smooth" })
}
// end support check error
const randomText = () => {
  const chars = "abcdefghijklmnopqrstuvwxyz"
  let text = Array.from(
    { length: 8 },
    () => chars[Math.floor(Math.random() * chars.length)]
  ).join("")
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const onChangeLang = (lang) => {
  const locale = langTypes.find((x) => x.id === lang)
  if (locale) {
    const urlObj = new URL(window.location.href)
    urlObj.searchParams.set("lang", locale.name) // Replace 'en' with your desired language
    const newUrl = urlObj.toString()
    window.location.href = newUrl
  }
}

const onSubmit = async () => {
  try {
    const { valid: isValid, errors } = await refVForm.value?.validate()
    if (isValid) {
      loadingSubmit.value = true
      // await showCustomSwal({
      //   icon: "success",
      // });
      const response = await StudentService.updateSurvey({
        id: info.value?.id,
        answers: selectedAnswers.value,
        nickName: nickName.value,
        hash: params.id,
      })

      if (response) {
        if (formType.value?.id === 6) {
          await showCustomSwal({
            html: `
            <p style="font-size: 18px;" class="text-black">
              ${$gettext("Thank you for taking the time to complete this survey.")}
            </p>
            <p style="font-size: 14px; color: gray; font-style: italic; font-weight: normal;" class="mb-0 text-center">
                ${$gettext("If you did not receive a confirmation email after submitting, ")} ${$gettext("your answers were not recorded.")} <br/> ${$gettext("Please contact")} <a class="text-info" href="mailto:mastersurvey@eduniversal-group.com">mastersurvey@eduniversal-group.com</a>  
            </p>
            `,
            showCancelButton: false,
            showConfirmButton: false,
            showCloseButton: true,
            timerProgressBar: true,
            timer: 10000,
            width: 800,
            position: "center",
          })
        } else {
          await showCustomSwal({
            html: `
            <p style="font-size: 14px;" class="mb-0">
              <b>${$gettext("Thank you for your participation!")}</b> <br/>
              ${$gettext("Your response has been recorded.")} <br/>
            </p>
            `,
            showCancelButton: false,
            showConfirmButton: false,
            showCloseButton: true,
            timerProgressBar: true,
            timer: 10000,
            width: 490,
            position: "center",
            willClose: () => {
              useCookie("lang").value = "en_US"
              window.location.reload();
              // if (response.redirectUrl)
              //   window.location.href = response.redirectUrl
              // else router.push("/")
            },
          })
        }
        answered.value = true
      } else {
        await showCustomSwal({
          html: `<div class="font-weight-regular">${error.value}</div>`,
          showCancelButton: false,
          showConfirmButton: false,
          showCloseButton: true,
          timerProgressBar: true,
          timer: 10000,
          width: 490,
          position: "center",
        })
        messageNoti.value = error.value
      }

      loadingSubmit.value = false
    } else {
      const firstError = errors[0]
      const inputElement = document.getElementById(firstError.id + "-messages")
      const titleElement = findQuestionTitle(inputElement)

      if (titleElement) {
        await showCustomSwal({
          showCancelButton: false,
          showConfirmButton: false,
          showCloseButton: true,
          timerProgressBar: true,
          timer: 10000,
          width: 490,
          position: "center",
          html: `<p>${$gettext("The question")}: <b>${
            titleElement.innerHTML
          }</b> ${firstError.errorMessages[0]} </p>`,
        })
        setTimeout(() => {
          scrollToElement(titleElement)
        }, 300)
      }

      return
    }
  } catch (error) {
    errorData.value = error?.data
  }
}
const formType = ref(null)
onMounted(async () => {
  if (+CAMPAIGN == 0 && !isAdmin.value) {
    router.push("/")
    return
  }
  try {
    loading.value = true
    // formType: {id: 6, name: "DBA Student Satisfaction Survey"}
    const res = await StudentService.getSurvey(params.id)
    useSurveyStore().setSurvey(res)
    steps.value = res?.steps || []
    openedPanels.value = steps.value.map((_, i) => i)
    info.value = {
      id: res?.id,
      formType: res?.formType,
      program: res?.program,
    }
    formType.value = res?.formType
    nextTick(() => {
      selectedAnswers.value = { ...res?.answers }
      nickName.value = res?.surveyor?.contact?.nickName || null
      answered.value = res?.isValidated ? true : false
    })
  } catch (error) {
    errorData.value = error?.data
  } finally {
    loading.value = false
  }
})
</script>
<template>
  <Waiting v-if="loading" :show="loading" />
  <div v-else class="register-student">
    <div class="register-student__container">
      <div class="register-student__bg">
        <img src="@/assets/images/palm_bg.svg" alt="palm" style="top: 0" />
      </div>
      <!-- Start Header -->
      <header
        class="d-flex align-center justify-center register-student__container-header"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img
            src="@/assets/logo-eduniversal.png"
            alt="Logo"
            style="max-width: 140px"
          />
        </RouterLink>
        <h1 class="title">
          {{ $gettext("Eduniversal Programs Surveys") }} {{ CURRENT_YEAR }}
        </h1>

        <!-- <BtnLang @onChange="onChangeLang" /> -->
      </header>
      <!-- End Header -->
      <main class="register-student__main" style="background: #fff">
        <ContentError v-if="errorData?.code" :code="errorData?.code" />
        <template v-else>
          <!-- Start Top -->
          <div class="form__title" style="max-width: none">
            <h3 class="mt-4" v-if="info.formType?.id !== 6">
              {{ CURRENT_YEAR }}
              Student Satisfaction Survey of the Best Master’s, MS, and MBA Programs
            </h3>
            <div class="mt-4" v-else>
              <h3 class="uppercase" v-html="formatFormTypeName(info.formType?.name)"></h3>
            </div>

            <InfoBlockList
              v-if="info.formType?.id === 6"
              :infoBlocks="dbaIntroBlock"
              class="text-left"
            />

            <InfoBlockList :infoBlocks="block1" class="text-left" />

            <h4>
              This form concerns:
            </h4>
            <h3>
              {{ info.program?.school?.name }}
            </h3>
            <h3>
              {{ info.program?.name }}
            </h3>
          </div>
          <!-- End Top -->
          <div class="py-6">
            <VForm
              ref="refVForm"
              :style="{
                'pointer-events': answered && !isAdmin ? 'none' : 'all',
              }"
            >
              <template v-if="steps.length === 1">
                <InfoBlockList
                  v-if="steps[0].infoBlocks?.length"
                  :infoBlocks="steps[0].infoBlocks"
                  class="mb-4"
                />
                <question-wrap
                  v-for="question in steps[0].questions"
                  :key="question.id"
                  :question="question"
                  v-model:selectedAnswers="selectedAnswers"
                />
              </template>

              <VExpansionPanels v-else v-model="openedPanels" multiple variant="accordion" disabled class="accordion-survey">
                <VExpansionPanel
                  v-for="step in steps"
                  :key="step.id"
                  :disabled="step.disabled"
                  elevation="0"
                >
                  <VExpansionPanelTitle class="text-h6 font-weight-bold text-uppercase py-4">
                    {{ step.title }}
                  </VExpansionPanelTitle>
                  <VExpansionPanelText>
                    <InfoBlockList
                      v-if="step.infoBlocks?.length"
                      :infoBlocks="step.infoBlocks"
                      class="mb-4"
                    />
                    <question-wrap
                      v-for="question in step.questions"
                      :key="question.id"
                      :question="question"
                      v-model:selectedAnswers="selectedAnswers"
                    />
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>

              <div class="text-center">
                <SubmitBtn
                  class="mt-8"
                  @click="onSubmit"
                  :loading="loadingSubmit"
                  :disabled="answered && !isAdmin"
                >
                  {{ $gettext("Submit my form") }}
                </SubmitBtn>
              </div>
            </VForm>
          </div>
        </template>
      </main>
    </div>
  </div>

  <Notification
    v-model:isDialogVisible="isConfirmDialogVisible"
    :success="successNoti"
    :error="errorNoti"
    :message="messageNoti"
  />
  <VCard style="margin: 30px 16px 50px 16px">
    <ContactWithUs />
  </VCard>
</template>

<style lang="scss">
@import "@/assets/styles/pages/register-student.scss";
</style>
